import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { of, throwError, delay } from 'rxjs';
import { User, UserRole } from '../core/models/user.model';
import { AuthUser, LoginRequest, LoginResponse } from '../core/models/auth.model';

/**
 * Mock Auth Handler
 * Intercepta las peticiones a /api/auth/* y devuelve respuestas simuladas
 */
export const authMockHandler: HttpInterceptorFn = (req, next) => {
  const { url, method, body } = req;

  // POST /api/auth/login
  if (url.endsWith('/api/v1/auth/login') && method === 'POST') {
    const loginReq = body as LoginRequest;
    return handleLogin(loginReq);
  }

  // POST /api/auth/logout
  if (url.endsWith('/api/v1/auth/logout') && method === 'POST') {
    return handleLogout();
  }

  // GET /api/auth/me
  if (url.endsWith('/api/v1/auth/me') && method === 'GET') {
    return handleMe();
  }

  // PATCH /api/auth/update-me
  if (url.endsWith('/api/v1/auth/update-me') && method === 'PATCH') {
    return handleUpdateMe(body as Partial<User>);
  }

  // POST /api/auth/forgot-password
  if (url.endsWith('/api/v1/auth/forgot-password') && method === 'POST') {
    return handleForgotPassword(body as { email: string });
  }

  // POST /api/auth/reset-password
  if (url.endsWith('/api/v1/auth/reset-password') && method === 'POST') {
    return handleResetPassword(
      body as { token: string; password: string; password_confirmation: string }
    );
  }

  // Si no es una ruta de auth, continuar con la petición normal
  return next(req);
};

function handleLogin(loginReq: LoginRequest) {
  const { email, password } = loginReq;

  const cleanEmail = (email ?? '').trim().toLowerCase();

  // Validaciones
  if (!cleanEmail || !password) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Email and password are required.' },
          url: '/api/auth/login',
        })
    ).pipe(delay(300));
  }

  // Mock: password debe ser "demo"
  if (password !== 'demo') {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Unauthorized',
          error: { message: 'Invalid credentials.' },
          url: '/api/auth/login',
        })
    ).pipe(delay(300));
  }

  // Determinar rol según email
  const role: UserRole = cleanEmail.includes('admin') ? 'admin' : 'partner';

  const user: AuthUser = {
    id: crypto?.randomUUID?.() ?? String(Date.now()),
    name: role === 'admin' ? 'Admin User' : 'Partner User',
    email: cleanEmail,
    roles: [role],
  };

  const response: LoginResponse = {
    user,
    token: `mock-token-${Date.now()}`,
    token_type: 'Bearer',
  };

  return of(
    new HttpResponse({
      status: 200,
      body: response,
    })
  ).pipe(delay(400));
}

function handleLogout() {
  return of(
    new HttpResponse({
      status: 204,
    })
  ).pipe(delay(200));
}

function handleMe() {
  // Simular obtención del usuario actual
  const user: AuthUser = {
    id: '123e4567-e89b-12d3-a456-426614174000',
    name: 'Partner User',
    email: 'partner@example.com',
    roles: ['partner'],
  };

  return of(
    new HttpResponse({
      status: 200,
      body: { data: user },
    })
  ).pipe(delay(300));
}

function handleUpdateMe(data: Partial<User>) {
  // Simular actualización del usuario actual
  const updatedUser: User = {
    id: '123e4567-e89b-12d3-a456-426614174000',
    name: data.name || 'Partner User',
    email: data.email || 'partner@example.com',
    phone: data.phone || '123-456-7890',
    company: data.company || 'Example Corp',
    roles: ['partner'],
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
    active: true,
    invitation_sent_at: new Date().toISOString(),
  };

  return of(
    new HttpResponse({
      status: 200,
      body: { data: updatedUser },
    })
  ).pipe(delay(400));
}

function handleForgotPassword(data: { email: string }) {
  const { email } = data;

  // Validar que el email esté presente
  if (!email || !email.trim()) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Email is required.' },
          url: '/api/auth/forgot-password',
        })
    ).pipe(delay(300));
  }

  // Validar formato de email
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Invalid email format.' },
          url: '/api/auth/forgot-password',
        })
    ).pipe(delay(300));
  }

  // Simular envío de email con éxito
  return of(
    new HttpResponse({
      status: 200,
      body: {
        message: 'Password reset link has been sent to your email.',
      },
    })
  ).pipe(delay(500));
}

function handleResetPassword(data: {
  token: string;
  password: string;
  password_confirmation: string;
}) {
  const { token, password, password_confirmation } = data;

  // Validaciones
  if (!token || !token.trim()) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Reset token is required.' },
          url: '/api/auth/reset-password',
        })
    ).pipe(delay(300));
  }

  if (!password || password.length < 8) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Password must be at least 8 characters long.' },
          url: '/api/auth/reset-password',
        })
    ).pipe(delay(300));
  }

  if (password !== password_confirmation) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Passwords do not match.' },
          url: '/api/auth/reset-password',
        })
    ).pipe(delay(300));
  }

  // Simular token inválido o expirado (opcional, para testing)
  if (token === 'invalid-token') {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 400,
          statusText: 'Bad Request',
          error: { message: 'Invalid or expired reset token.' },
          url: '/api/auth/reset-password',
        })
    ).pipe(delay(300));
  }

  // Simular reseteo exitoso
  return of(
    new HttpResponse({
      status: 200,
      body: {
        message: 'Password has been reset successfully.',
      },
    })
  ).pipe(delay(500));
}
